import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  Input,
  Textarea,
  useDisclosure,
  useToast,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Stack,
} from '@chakra-ui/react'
import {
  DocumentReference,
  arrayUnion,
  doc,
  getDoc,
  getFirestore,
  updateDoc,
} from 'firebase/firestore'
import { map } from 'lodash'
import { useEffect, useRef, useState, useCallback } from 'react'
import useHandleError from '../../../../hooks/useHandleError'
import FirestoreUser from '../../../../types/FirestoreUser'
import ConfigList from '../../../../types/ConfigList'
import { normalizeUserStudies } from '../../../../utils/studyHelpers'
import {
  ACCORDION_BORDER_STYLE,
  ACCORDION_BUTTON_STYLE,
} from '../constants'
import { primaryServices } from '../../../../utils/firebaseServiceContext'

export interface StudyData {
  name: string
  status: 'Applicant' | 'Participant' | 'Withdrawn' | 'Completed'
  startDate?: string
  endDate?: string
  reason?: string // categorical reason (withdrawn/completed)
  details?: string // free text details
}

interface StudyChangeLogEntry {
  studyName: string
  status: StudyData['status']
  date: string
  reason?: string
  details?: string
}

const WITHDRAW_COMPLETION_REASONS = [
  'Deceased',
  'Lost to follow-up',
  'Voluntary withdrawal',
  'Involuntary withdrawal',
]

export const Studies = ({
  uid,
  onUpdate,
  editStudyId,
  setEditStudyId,
}: {
  uid: string
  onUpdate?: () => void
  editStudyId?: string | null
  setEditStudyId?: (id: string | null) => void
}) => {
  const { isOpen, onOpen, onClose: _onClose } = useDisclosure()
  const editModalDisclosure = useDisclosure()
  const cancelRef = useRef<any>()
  const handleError = useHandleError()
  const [selectedStudy, setSelectedStudy] = useState('')
  const [studies, setStudies] = useState<{ itemId: string; text: string }[]>([])
  const [studyData, setStudyData] = useState<StudyData[]>([])
  const [studyChangeLogs, setStudyChangeLogs] = useState<StudyChangeLogEntry[]>(
    []
  )
  const [loading, setLoading] = useState(false)
  const toast = useToast()

  const [editingStudy, setEditingStudy] = useState<StudyData | null>(null)
  const [editStatus, setEditStatus] =
    useState<StudyData['status']>('Participant')
  const [editStartDate, setEditStartDate] = useState('')
  const [editEndDate, setEditEndDate] = useState('')
  const [editReason, setEditReason] = useState('')
  const [editDetails, setEditDetails] = useState('')

  // Load available studies list
  useEffect(() => {
    const fetchStudies = async () => {
      const ref = doc(
        primaryServices.firestore,
        'config',
        'studies'
      ) as DocumentReference<ConfigList>
      const snap = await getDoc(ref)
      const data = snap.data()
      if (data) {
        const items = map(data.value, (text, itemId) => ({ itemId, text }))
        setStudies(items)
      }
    }
    fetchStudies().catch(handleError)
  }, [handleError])

  // Load user studies & logs
  useEffect(() => {
    const fetchUserStudies = async () => {
      try {
        const userRef = doc(
          primaryServices.firestore,
          'users',
          uid
        ) as DocumentReference<FirestoreUser>
        const userSnap = await getDoc(userRef)
        const userData = userSnap.data()
        if (userData?.studies) {
          const formatted = normalizeUserStudies(userData.studies)
          setStudyData(formatted)
        }
        if (userData?.studyChangeLogs) {
          const logs: StudyChangeLogEntry[] = userData.studyChangeLogs as any
          logs.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          )
          setStudyChangeLogs(logs)
        }
      } catch (e) {
        handleError(e)
      }
    }
    fetchUserStudies()
  }, [uid, handleError])

  const onClose = () => {
    setSelectedStudy('')
    _onClose()
  }

  const closeEditModal = () => {
    setEditingStudy(null)
    setEditStatus('Participant')
    setEditStartDate('')
    setEditEndDate('')
    setEditReason('')
    setEditDetails('')
    editModalDisclosure.onClose()
  }

  const openEditModal = useCallback(
    (study: StudyData) => {
      setEditingStudy(study)
      setEditStatus(study.status || 'Participant')
      setEditStartDate(study.startDate || '')
      setEditEndDate(study.endDate || '')
      setEditReason(study.reason || '')
      setEditDetails(study.details || '')
      editModalDisclosure.onOpen()
    },
    [editModalDisclosure]
  )

  // Add new study (Applicant)
  const handleSubmit = async () => {
    setLoading(true)
    try {
      const userRef = doc(
        primaryServices.firestore,
        'users',
        uid
      ) as DocumentReference<FirestoreUser>
      const newStudy: StudyData = { name: selectedStudy, status: 'Applicant' }
      const logEntry: StudyChangeLogEntry = {
        studyName: selectedStudy,
        status: 'Applicant',
        date: new Date().toISOString(),
      }
      await updateDoc(userRef, {
        studies: arrayUnion(newStudy),
        studyChangeLogs: arrayUnion(logEntry),
      })
      setStudyData((prev) => [...prev, newStudy])
      setStudyChangeLogs((prev) => [logEntry, ...prev])
      onClose()
      onUpdate?.()
      toast({ status: 'success', description: 'Successfully added study.' })
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  // Edit existing study
  const handleEditSubmit = async () => {
    if (!editingStudy) return

    // Date validation only at submit
    if (
      (editStatus === 'Withdrawn' || editStatus === 'Completed') &&
      editStartDate &&
      editEndDate &&
      editEndDate < editStartDate
    ) {
      toast({
        status: 'error',
        description: 'End date cannot be before start date.',
      })
      return
    }

    setLoading(true)
    try {
      const userRef = doc(
        primaryServices.firestore,
        'users',
        uid
      ) as DocumentReference<FirestoreUser>
      const snap = await getDoc(userRef)
      const data = snap.data()
      let current: StudyData[] = []
      if (data?.studies) {
        current = (data.studies as any[]).map((s) =>
          typeof s === 'string'
            ? { name: s, status: 'Participant' }
            : (s as StudyData)
        )
      }

      const updated: StudyData = {
        name: editingStudy.name,
        status: editStatus,
        ...(editStartDate && { startDate: editStartDate }),
        ...(editStatus !== 'Participant'
          ? {
              endDate: editEndDate || new Date().toISOString().split('T')[0],
              ...(editReason && { reason: editReason }),
              ...(editDetails && { details: editDetails }),
            }
          : {}),
      }

      const newStudies = current
        .filter((s) => s.name !== editingStudy.name)
        .concat(updated)

      const logEntry: StudyChangeLogEntry = {
        studyName: editingStudy.name,
        status: editStatus,
        date: new Date().toISOString(),
        ...(editStatus === 'Withdrawn' || editStatus === 'Completed'
          ? {
              ...(editReason && { reason: editReason }),
              ...(editDetails && { details: editDetails }),
            }
          : {}),
      }

      await updateDoc(userRef, {
        studies: newStudies,
        studyChangeLogs: arrayUnion(logEntry),
      })

      setStudyData(newStudies)
      setStudyChangeLogs((prev) => [logEntry, ...prev])
      closeEditModal()
      onUpdate?.()
      toast({ status: 'success', description: 'Successfully updated study.' })
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const isSaveButtonDisabled = () => {
    if (loading) return true
    if (editStatus === 'Withdrawn' || editStatus === 'Completed') {
      if (!editEndDate || !editReason) return true
    }
    return false
  }

  // External trigger
  useEffect(() => {
    if (editStudyId && studyData.length) {
      const target = studyData.find((s) => s.name === editStudyId)
      if (target) {
        openEditModal(target)
        setEditStudyId?.(null)
      }
    }
  }, [editStudyId, studyData, openEditModal, setEditStudyId])

  return (
    <>
      <Box>
        <Button variant='link' onClick={onOpen}>
          Add Studies
        </Button>
      </Box>

      {/* Studies Table */}
      <Box mt={4}>
        <Heading size='sm' mb={2}>
          Studies
        </Heading>
        <Table size='sm' variant='simple'>
          <Thead>
            <Tr>
              <Th>Study</Th>
              <Th>Status</Th>
              <Th>Start</Th>
              <Th>End</Th>
              <Th>Reason</Th>
              <Th>Details</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {studyData.map((s) => (
              <Tr key={s.name}>
                <Td>{s.name}</Td>
                <Td>{s.status}</Td>
                <Td>{s.startDate || '-'}</Td>
                <Td>{s.endDate || '-'}</Td>
                <Td>{s.reason || '-'}</Td>
                <Td maxW='250px' whiteSpace='normal'>
                  {s.details || '-'}
                </Td>
                <Td>
                  <Button size='xs' onClick={() => openEditModal(s)}>
                    Edit
                  </Button>
                </Td>
              </Tr>
            ))}
            {studyData.length === 0 && (
              <Tr>
                <Td colSpan={7}>
                  <Text fontSize='sm' color='gray.600'>
                    No studies recorded.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Add Study Modal */}
      <AlertDialog
        isOpen={isOpen}
        onClose={onClose}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Add Study</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <FormControl mb={3}>
                <FormLabel>Study</FormLabel>
                <Select
                  placeholder='Select study'
                  value={selectedStudy}
                  onChange={(e) => setSelectedStudy(e.target.value)}
                >
                  {studies.map((s) => (
                    <option key={s.itemId} value={s.text}>
                      {s.text}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} colorScheme='gray'>
                Cancel
              </Button>
              <Button
                ml={3}
                isDisabled={loading || !selectedStudy}
                isLoading={loading}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Edit Study Modal */}
      <AlertDialog
        isOpen={editModalDisclosure.isOpen}
        onClose={closeEditModal}
        leastDestructiveRef={cancelRef}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>
              Edit Study: {editingStudy?.name}
            </AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              <FormControl mb={3}>
                <FormLabel>Status</FormLabel>
                <Select
                  value={editStatus}
                  onChange={(e) =>
                    setEditStatus(
                      e.target.value as
                        | 'Applicant'
                        | 'Participant'
                        | 'Withdrawn'
                    )
                  }
                >
                  <option value='Applicant'>Applicant</option>
                  <option value='Participant'>Participant</option>
                  <option value='Withdrawn'>Withdrawn</option>
                </Select>
              </FormControl>

              {editStatus === 'Participant' && (
                <FormControl mb={3}>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type='date'
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                  />
                </FormControl>
              )}

              {(editStatus === 'Withdrawn' || editStatus === 'Completed') && (
                <>
                  <FormControl mb={3}>
                    <FormLabel>End Date</FormLabel>
                    <Input
                      type='date'
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                    />
                  </FormControl>
                  <FormControl mb={3}>
                    <FormLabel>Reason</FormLabel>
                    <Select
                      placeholder='Select reason'
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                    >
                      {WITHDRAW_COMPLETION_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl mb={3}>
                    <FormLabel>Details</FormLabel>
                    <Textarea
                      value={editDetails}
                      onChange={(e) => setEditDetails(e.target.value)}
                      placeholder='Additional details (optional)'
                    />
                  </FormControl>
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={closeEditModal}
                colorScheme='gray'
              >
                Cancel
              </Button>
              <Button
                ml={3}
                colorScheme='blue'
                isDisabled={isSaveButtonDisabled()}
                isLoading={loading}
                onClick={handleEditSubmit}
              >
                Save Changes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Study Change History */}
      <Box mt={6}>
        <Accordion defaultIndex={[]} allowToggle>
          <AccordionItem {...ACCORDION_BORDER_STYLE}>
            <AccordionButton {...ACCORDION_BUTTON_STYLE}>
              <Box flex='1' textAlign='left'>
                <Text fontWeight='semibold'>Study Change History</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={3} pb={3}>
              {studyChangeLogs.length === 0 ? (
                <Text fontSize='sm' color='gray.600'>
                  No study change history available.
                </Text>
              ) : (
                <Table size='sm' variant='striped'>
                  <Thead>
                    <Tr>
                      <Th>Date</Th>
                      <Th>Study</Th>
                      <Th>Status</Th>
                      <Th>Reason</Th>
                      <Th>Details</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {studyChangeLogs.map((log, idx) => (
                      <Tr key={idx}>
                        <Td>{new Date(log.date).toLocaleString()}</Td>
                        <Td>{log.studyName}</Td>
                        <Td>{log.status}</Td>
                        <Td>{log.reason || '-'}</Td>
                        <Td maxW='250px' whiteSpace='normal'>
                          {log.details || '-'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Box>
    </>
  )
}
